import type { DrawerMode } from '@monorepo/shared/schemas/common'
import { DRAWER_MODE, PRICING_MODEL } from '@monorepo/shared/schemas/common'
import type { PlanAgentAction, TierInput } from '@monorepo/shared/schemas/plans'
import { DollarSign, Info, Loader2, Plus, Save, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useAgentActions } from '@/features/agents/hooks/useAgentActions'
import { useAgentsList } from '@/features/agents/hooks/useAgents'
import {
  type PricingModel,
  PricingModelSelect,
} from '@/shared/components/common/PricingModelSelect'
import { Alert, AlertDescription } from '@/shared/components/ui/alert'
import { Button } from '@/shared/components/ui/button'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/components/ui/drawer'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/shared/components/ui/tooltip'
import { toast } from '@/shared/lib/toast'
import {
  useAddAgentAction,
  usePlan,
  useUpdateAgentAction,
} from '../../hooks/usePlans'

interface AgentActionFormData {
  agent_action_id: string
  pricing_model: PricingModel
  unit_price: number | null
  package_size: number | null
  package_price: number | null
  included_units: number
  minimum_commitment: number
  overage_allowed: boolean
  tiers: TierInput[]
}

interface AgentActionDrawerProps {
  planId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  editingAction?: PlanAgentAction & { pricing_tiers?: TierInput[] }
  mode?: DrawerMode
}

export function AgentActionDrawer({
  planId,
  open,
  onOpenChange,
  editingAction,
  mode = DRAWER_MODE.create,
}: AgentActionDrawerProps) {
  // Fetch plan data to get already-added agent actions
  const { data: plan } = usePlan(planId)

  // Fetch data
  const { agents, isLoading: isLoadingAgents } = useAgentsList()
  const [selectedAgentId, setSelectedAgentId] = useState<string>('')
  const { data: actionsData, isLoading: isLoadingActions } = useAgentActions(
    selectedAgentId,
    { limit: 100 }
  )

  // Mutations
  const addAgentAction = useAddAgentAction()
  const updateAgentAction = useUpdateAgentAction()

  // Track already-added agent action IDs
  const addedAgentActionIds = useMemo(() => {
    return new Set(plan?.agent_actions?.map(aa => aa.agent_action_id) || [])
  }, [plan?.agent_actions])

  // Form state
  const [formData, setFormData] = useState<AgentActionFormData>({
    agent_action_id: '',
    pricing_model: PRICING_MODEL.flat,
    unit_price: null,
    package_size: null,
    package_price: null,
    included_units: 0,
    minimum_commitment: 0,
    overage_allowed: true,
    tiers: [],
  })

  // Initialize form when editing
  useEffect(() => {
    if (mode === DRAWER_MODE.edit && editingAction && open) {
      // Filter pricing model to only supported values
      const pricingModel = (
        (
          [
            PRICING_MODEL.flat,
            PRICING_MODEL.tiered,
            PRICING_MODEL.volume,
            PRICING_MODEL.package,
          ] as string[]
        ).includes(editingAction.pricing_model as string)
          ? editingAction.pricing_model
          : PRICING_MODEL.flat
      ) as PricingModel

      setFormData({
        agent_action_id: editingAction.agent_action_id,
        pricing_model: pricingModel,
        unit_price: editingAction.unit_price,
        package_size: editingAction.package_size,
        package_price: editingAction.package_price,
        included_units: editingAction.included_units || 0,
        minimum_commitment: editingAction.minimum_commitment || 0,
        overage_allowed: editingAction.overage_allowed ?? true,
        tiers:
          editingAction.pricing_tiers?.map((t, index: number) => ({
            tier_order: t.tier_order || index + 1,
            start_unit: t.start_unit,
            end_unit: t.end_unit ?? null,
            price_per_unit: t.price_per_unit,
            flat_fee: t.flat_fee ?? null,
          })) || [],
      })
      // Set the agent ID for the action dropdown
      // Note: In edit mode, we might not have the agent_id directly, so we'll need to find it
      // For now, we'll keep the agent dropdown disabled in edit mode
    } else if (mode === DRAWER_MODE.create && open) {
      // Reset form for create mode
      setFormData({
        agent_action_id: '',
        pricing_model: PRICING_MODEL.flat,
        unit_price: null,
        package_size: null,
        package_price: null,
        included_units: 0,
        minimum_commitment: 0,
        overage_allowed: true,
        tiers: [],
      })
      setSelectedAgentId('')
    }
  }, [mode, editingAction, open])

  const handleSave = async () => {
    if (!editingAction && mode === DRAWER_MODE.edit) return

    // Validation
    if (!formData.agent_action_id) {
      toast.error('Please select an agent action')
      return
    }

    // Validate based on pricing model
    if (
      formData.pricing_model === PRICING_MODEL.flat &&
      (formData.unit_price === null || formData.unit_price === undefined)
    ) {
      toast.error('Please enter a unit price for flat rate pricing')
      return
    }
    if (formData.pricing_model === PRICING_MODEL.package) {
      if (!formData.package_size || formData.package_size <= 0) {
        toast.error('Please enter a valid package size')
        return
      }
      if (
        formData.package_price === null ||
        formData.package_price === undefined ||
        formData.package_price <= 0
      ) {
        toast.error('Please enter a valid package price')
        return
      }
    }
    if (
      (formData.pricing_model === PRICING_MODEL.tiered ||
        formData.pricing_model === PRICING_MODEL.volume) &&
      formData.tiers.length === 0
    ) {
      toast.error('Please add at least one pricing tier')
      return
    }

    try {
      // Prepare the data according to the API schema
      interface ApiData {
        agent_action_id: string
        pricing_model: PricingModel
        unit_price?: number
        package_size?: number
        package_price?: number
        included_units: number
        minimum_commitment: number
        overage_allowed: boolean
        tiers?: TierInput[]
        metadata: Record<string, unknown>
      }

      const apiData: ApiData = {
        agent_action_id: formData.agent_action_id,
        pricing_model: formData.pricing_model,
        included_units: formData.included_units,
        minimum_commitment: formData.minimum_commitment,
        overage_allowed: formData.overage_allowed,
        metadata: {},
      }

      // Add pricing-specific fields
      if (
        formData.pricing_model === PRICING_MODEL.flat &&
        formData.unit_price !== null
      ) {
        apiData.unit_price = formData.unit_price
      } else if (formData.pricing_model === PRICING_MODEL.package) {
        if (formData.package_size !== null)
          apiData.package_size = formData.package_size
        if (formData.package_price !== null)
          apiData.package_price = formData.package_price
      }

      // Add tiers for tiered/volume pricing
      if (
        (formData.pricing_model === PRICING_MODEL.tiered ||
          formData.pricing_model === PRICING_MODEL.volume) &&
        formData.tiers.length > 0
      ) {
        apiData.tiers = formData.tiers
      }

      if (mode === DRAWER_MODE.create) {
        await addAgentAction.mutateAsync({
          planId,
          data: apiData,
        })

        toast.success('Agent added to plan successfully')
      } else {
        await updateAgentAction.mutateAsync({
          planId,
          agentActionId: editingAction!.id,
          data: apiData,
        })

        toast.success('Agent updated successfully')
      }

      onOpenChange(false)
    } catch {
      // Error toast is handled by the mutation hooks
    }
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  const handleAddTier = () => {
    const newTier: TierInput = {
      tier_order: formData.tiers.length + 1,
      start_unit:
        formData.tiers.length > 0
          ? (formData.tiers[formData.tiers.length - 1].end_unit || 0) + 1
          : 0,
      end_unit: null,
      price_per_unit: 0,
      flat_fee: null,
    }
    setFormData(prev => ({
      ...prev,
      tiers: [...prev.tiers, newTier],
    }))
  }

  const handleRemoveTier = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tiers: prev.tiers
        .filter((_, i) => i !== index)
        .map((tier, i) => ({
          ...tier,
          tier_order: i + 1,
        })),
    }))
  }

  const handleTierChange = (
    index: number,
    field: keyof TierInput,
    value: number | null
  ) => {
    setFormData(prev => ({
      ...prev,
      tiers: prev.tiers.map((tier, i) =>
        i === index ? { ...tier, [field]: value } : tier
      ),
    }))
  }

  const actionsList = actionsData?.data || []
  const selectedAction = actionsList.find(
    action => action.id === formData.agent_action_id
  )
  const isSaving = addAgentAction.isPending || updateAgentAction.isPending

  // Filter actions to identify available vs already-added
  const availableActions = actionsList.filter(
    action => !addedAgentActionIds.has(action.id)
  )
  const allActionsAdded =
    selectedAgentId && actionsList.length > 0 && availableActions.length === 0

  return (
    <Drawer open={open} onOpenChange={onOpenChange} direction='right'>
      <DrawerContent className='h-full w-[50vw] min-w-[600px] max-w-[800px] ml-auto fixed right-0'>
        <DrawerHeader>
          <DrawerTitle>
            {mode === DRAWER_MODE.create ? 'Add Agent' : 'Edit Agent'}
          </DrawerTitle>
          <DrawerDescription>
            Configure pricing for agent action usage in this plan
          </DrawerDescription>
        </DrawerHeader>

        <div className='flex-1 overflow-y-auto px-4 pb-6'>
          <div className='space-y-6'>
            {/* Agent Selection */}
            <div>
              <Label htmlFor='agent'>Agent *</Label>
              <Select
                value={selectedAgentId}
                onValueChange={setSelectedAgentId}
                disabled={mode === DRAWER_MODE.edit || isLoadingAgents}
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue
                    placeholder={
                      isLoadingAgents ? 'Loading agents...' : 'Select an agent'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {agents.map(agent => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-500 mt-1'>
                {mode === DRAWER_MODE.edit
                  ? 'Agent cannot be changed after creation'
                  : 'Choose the agent whose actions you want to price'}
              </p>
            </div>

            {/* Agent Action Selection */}
            <div>
              <Label htmlFor='agent-action'>Agent Action *</Label>
              <Select
                value={formData.agent_action_id}
                onValueChange={value =>
                  setFormData(prev => ({ ...prev, agent_action_id: value }))
                }
                disabled={
                  mode === DRAWER_MODE.edit ||
                  !selectedAgentId ||
                  isLoadingActions
                }
              >
                <SelectTrigger className='mt-1'>
                  <SelectValue
                    placeholder={
                      selectedAgentId
                        ? isLoadingActions
                          ? 'Loading actions...'
                          : 'Select an action'
                        : 'Select an agent first'
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <TooltipProvider delayDuration={300}>
                    {actionsList.map(action => {
                      const isAlreadyAdded =
                        mode === DRAWER_MODE.create &&
                        addedAgentActionIds.has(action.id)
                      const actionLabel = `${action.action_name} (${action.action_type})`

                      return (
                        <Tooltip key={action.id}>
                          <TooltipTrigger asChild>
                            <SelectItem
                              value={action.id}
                              disabled={isAlreadyAdded}
                              className={
                                isAlreadyAdded
                                  ? 'opacity-50 cursor-not-allowed'
                                  : ''
                              }
                            >
                              {actionLabel}
                              {isAlreadyAdded && (
                                <span className='text-xs text-muted-foreground ml-2'>
                                  (Already added)
                                </span>
                              )}
                            </SelectItem>
                          </TooltipTrigger>
                          {isAlreadyAdded && (
                            <TooltipContent side='right'>
                              <p>Already added to this plan</p>
                            </TooltipContent>
                          )}
                        </Tooltip>
                      )
                    })}
                  </TooltipProvider>
                </SelectContent>
              </Select>
              <p className='text-xs text-slate-500 mt-1'>
                {mode === DRAWER_MODE.edit
                  ? 'Agent action cannot be changed after creation'
                  : 'The specific action to charge for'}
              </p>

              {/* Alert when all actions are already added */}
              {mode === DRAWER_MODE.create && allActionsAdded && (
                <Alert className='mt-3 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800'>
                  <Info className='h-4 w-4 text-blue-600 dark:text-blue-400' />
                  <AlertDescription className='text-blue-800 dark:text-blue-200'>
                    All available agent actions for this agent have been added
                    to this plan.
                  </AlertDescription>
                </Alert>
              )}
            </div>

            {/* Billable Signal - Read Only - Only show after action is selected */}
            {formData.agent_action_id && (
              <div>
                <Label>Billable Signal</Label>
                <div className='mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-md border border-slate-200 dark:border-slate-700'>
                  {selectedAction?.billable_signal ? (
                    <div className='space-y-1'>
                      <div className='text-sm font-medium text-slate-900 dark:text-slate-100'>
                        {selectedAction.billable_signal.signal_name}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        Code: {selectedAction.billable_signal.signal_code} •
                        Type: {selectedAction.billable_signal.aggregation_type}{' '}
                        • Unit: {selectedAction.billable_signal.unit_label}
                      </div>
                    </div>
                  ) : mode === DRAWER_MODE.edit &&
                    editingAction?.billable_signal ? (
                    <div className='space-y-1'>
                      <div className='text-sm font-medium text-slate-900 dark:text-slate-100'>
                        {editingAction.billable_signal.signal_name}
                      </div>
                      <div className='text-xs text-muted-foreground'>
                        Code: {editingAction.billable_signal.signal_code} •
                        Type: {editingAction.billable_signal.aggregation_type} •
                        Unit: {editingAction.billable_signal.unit_label}
                      </div>
                    </div>
                  ) : (
                    <div className='text-sm text-muted-foreground'>
                      No billable signal configured for this action
                    </div>
                  )}
                </div>
                <p className='text-xs text-slate-500 mt-1'>
                  The billable signal is defined by the agent action and cannot
                  be changed
                </p>
              </div>
            )}

            <div className='border-t pt-6'></div>

            {/* Pricing Model */}
            <div>
              <Label>Pricing Model *</Label>
              <PricingModelSelect
                value={formData.pricing_model}
                onValueChange={value =>
                  setFormData(prev => ({
                    ...prev,
                    pricing_model: value,
                    // Reset pricing fields when model changes
                    unit_price:
                      value === PRICING_MODEL.flat ? prev.unit_price : null,
                    package_size:
                      value === PRICING_MODEL.package
                        ? prev.package_size
                        : null,
                    package_price:
                      value === PRICING_MODEL.package
                        ? prev.package_price
                        : null,
                    tiers:
                      value === PRICING_MODEL.tiered ||
                      value === PRICING_MODEL.volume
                        ? prev.tiers
                        : [],
                  }))
                }
                className='mt-0'
              />
            </div>

            {/* Flat Rate Pricing */}
            {formData.pricing_model === PRICING_MODEL.flat && (
              <div>
                <Label htmlFor='unit-price'>Unit Price *</Label>
                <div className='relative mt-1'>
                  <DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                  <Input
                    id='unit-price'
                    type='number'
                    step='0.01'
                    value={formData.unit_price ?? ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        unit_price: e.target.value
                          ? parseFloat(e.target.value)
                          : null,
                      }))
                    }
                    placeholder='0.00'
                    className='pl-9'
                  />
                </div>
                <p className='text-xs text-slate-500 mt-1'>
                  Price per unit (e.g., $0.10 per API call)
                </p>
              </div>
            )}

            {/* Package Pricing */}
            {formData.pricing_model === PRICING_MODEL.package && (
              <div className='space-y-4'>
                <div>
                  <Label htmlFor='package-size'>Package Size *</Label>
                  <Input
                    id='package-size'
                    type='number'
                    value={formData.package_size ?? ''}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        package_size: e.target.value
                          ? parseInt(e.target.value)
                          : null,
                      }))
                    }
                    placeholder='1000'
                    className='mt-1'
                  />
                  <p className='text-xs text-slate-500 mt-1'>
                    Number of units in the package
                  </p>
                </div>
                <div>
                  <Label htmlFor='package-price'>Package Price *</Label>
                  <div className='relative mt-1'>
                    <DollarSign className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                    <Input
                      id='package-price'
                      type='number'
                      step='0.01'
                      value={formData.package_price ?? ''}
                      onChange={e =>
                        setFormData(prev => ({
                          ...prev,
                          package_price: e.target.value
                            ? parseFloat(e.target.value)
                            : null,
                        }))
                      }
                      placeholder='100.00'
                      className='pl-9'
                    />
                  </div>
                  <p className='text-xs text-slate-500 mt-1'>
                    Fixed price for the package
                  </p>
                </div>
                {formData.package_size && formData.package_price && (
                  <div className='bg-slate-100 dark:bg-slate-800 p-3 rounded-lg'>
                    <p className='text-sm text-slate-700 dark:text-slate-300'>
                      <strong>Effective rate:</strong> $
                      {(formData.package_price / formData.package_size).toFixed(
                        4
                      )}{' '}
                      per unit
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Tiered/Volume Pricing */}
            {(formData.pricing_model === PRICING_MODEL.tiered ||
              formData.pricing_model === PRICING_MODEL.volume) && (
              <div>
                <div className='flex items-center justify-between mb-4'>
                  <Label className='text-base'>Pricing Tiers *</Label>
                  <Button
                    type='button'
                    onClick={handleAddTier}
                    size='sm'
                    variant='outline'
                  >
                    <Plus className='w-4 h-4 mr-2' />
                    Add Tier
                  </Button>
                </div>

                {formData.tiers.length === 0 ? (
                  <div className='border border-dashed rounded-lg p-8 text-center text-sm text-muted-foreground'>
                    No tiers added yet. Click "Add Tier" to get started.
                  </div>
                ) : (
                  <>
                    {/* Tier headers */}
                    <div className='grid grid-cols-12 gap-2 mb-2 text-sm font-medium text-muted-foreground'>
                      <div className='col-span-2'>Start unit</div>
                      <div className='col-span-2'>End unit</div>
                      <div className='col-span-3'>Per unit price</div>
                      <div className='col-span-3'>Flat fee</div>
                      <div className='col-span-2'></div>
                    </div>

                    {formData.tiers.map((tier, index) => (
                      <div
                        // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton
                        key={index}
                        className='grid grid-cols-12 gap-2 mb-2 items-center'
                      >
                        <div className='col-span-2'>
                          <Input
                            type='number'
                            value={tier.start_unit}
                            onChange={e =>
                              handleTierChange(
                                index,
                                'start_unit',
                                parseInt(e.target.value) || 0
                              )
                            }
                            placeholder='0'
                            className='text-sm'
                          />
                        </div>
                        <div className='col-span-2'>
                          <Input
                            type='number'
                            value={tier.end_unit ?? ''}
                            onChange={e =>
                              handleTierChange(
                                index,
                                'end_unit',
                                e.target.value ? parseInt(e.target.value) : null
                              )
                            }
                            placeholder={
                              index === formData.tiers.length - 1 ? '∞' : '100'
                            }
                            className='text-sm'
                          />
                        </div>
                        <div className='col-span-3'>
                          <div className='relative'>
                            <DollarSign className='absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground' />
                            <Input
                              type='number'
                              step='0.01'
                              value={tier.price_per_unit}
                              onChange={e =>
                                handleTierChange(
                                  index,
                                  'price_per_unit',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              placeholder='0.00'
                              className='pl-6 text-sm'
                            />
                          </div>
                        </div>
                        <div className='col-span-3'>
                          <div className='relative'>
                            <DollarSign className='absolute left-2 top-1/2 transform -translate-y-1/2 w-3 h-3 text-muted-foreground' />
                            <Input
                              type='number'
                              step='0.01'
                              value={tier.flat_fee ?? ''}
                              onChange={e =>
                                handleTierChange(
                                  index,
                                  'flat_fee',
                                  e.target.value
                                    ? parseFloat(e.target.value)
                                    : null
                                )
                              }
                              placeholder='0'
                              className='pl-6 text-sm'
                            />
                          </div>
                        </div>
                        <div className='col-span-2 flex justify-end'>
                          <Button
                            type='button'
                            variant='outline'
                            size='sm'
                            onClick={() => handleRemoveTier(index)}
                          >
                            <Trash2 className='w-4 h-4' />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            <div className='border-t pt-6'></div>

            {/* Additional Settings */}
            <div className='space-y-4'>
              <h4 className='font-medium text-sm'>Additional Settings</h4>

              <div>
                <Label htmlFor='included-units'>Included Units</Label>
                <Input
                  id='included-units'
                  type='number'
                  value={formData.included_units}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      included_units: parseInt(e.target.value) || 0,
                    }))
                  }
                  placeholder='0'
                  className='mt-1'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Units included in the base plan price (free allowance)
                </p>
              </div>

              <div>
                <Label htmlFor='minimum-commitment'>Minimum Commitment</Label>
                <Input
                  id='minimum-commitment'
                  type='number'
                  value={formData.minimum_commitment}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      minimum_commitment: parseInt(e.target.value) || 0,
                    }))
                  }
                  placeholder='0'
                  className='mt-1'
                />
                <p className='text-xs text-slate-500 mt-1'>
                  Minimum units customer must pay for regardless of usage
                </p>
              </div>

              <div className='flex items-center justify-between'>
                <div>
                  <Label htmlFor='overage-allowed'>Allow Overage</Label>
                  <p className='text-xs text-slate-500 mt-1'>
                    Allow usage beyond limits
                  </p>
                </div>
                <div
                  className={`w-10 h-5 rounded-full relative transition-colors cursor-pointer ${
                    formData.overage_allowed
                      ? 'bg-green-500'
                      : 'bg-slate-300 dark:bg-slate-600'
                  }`}
                  onClick={() =>
                    setFormData(prev => ({
                      ...prev,
                      overage_allowed: !prev.overage_allowed,
                    }))
                  }
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ')
                      setFormData(prev => ({
                        ...prev,
                        overage_allowed: !prev.overage_allowed,
                      }))
                  }}
                  role='switch'
                  aria-checked={formData.overage_allowed}
                  tabIndex={0}
                >
                  <div
                    className={`w-4 h-4 bg-white rounded-full absolute top-0.5 transition-transform ${
                      formData.overage_allowed
                        ? 'translate-x-5'
                        : 'translate-x-0.5'
                    }`}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DrawerFooter>
          <div className='flex gap-2 w-full'>
            <DrawerClose asChild>
              <Button
                variant='outline'
                onClick={handleCancel}
                disabled={isSaving}
                className='flex-1'
              >
                <X className='w-4 h-4 mr-2' />
                Cancel
              </Button>
            </DrawerClose>
            <Button
              onClick={handleSave}
              disabled={isSaving || !formData.agent_action_id}
              className='flex-1'
            >
              {isSaving && <Loader2 className='w-4 h-4 mr-2 animate-spin' />}
              <Save className='w-4 h-4 mr-2' />
              {mode === DRAWER_MODE.create ? 'Add to Plan' : 'Save Changes'}
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
